import posthog from 'posthog-js';

const DEFAULT_API_HOST = 'https://us.i.posthog.com';

/**
 * Query params that must never reach PostHog. The app is reached through URLs
 * carrying live credentials — a GoStock connection key (`key`), the SSO
 * one-time token, and Shopify's own signed params — and `$current_url` is
 * attached to every event and shown alongside every session replay.
 */
const SENSITIVE_PARAMS = [
  'key',
  'oneTimeLoginToken',
  'clientId',
  'hmac',
  'signature',
  'id_token',
  'session',
];

const REDACTED = '[redacted]';

/** Replaces the value of any sensitive param, leaving the rest of the URL readable. */
export function scrubSensitiveUrl(value) {
  if (typeof value !== 'string' || !value.includes('?')) return value;

  try {
    const url = new URL(value, window.location.origin);
    const hits = SENSITIVE_PARAMS.filter(param => url.searchParams.has(param));
    if (hits.length === 0) return value;

    hits.forEach(param => url.searchParams.set(param, REDACTED));
    return url.toString();
  } catch {
    // Not a parseable URL — drop the query rather than risk passing a key on.
    return value.split('?')[0];
  }
}

/**
 * Scrubs every URL-shaped property, not just `$current_url`: PostHog also sends
 * `$initial_current_url`, `$session_entry_url` and `$external_click_url`, any of
 * which can carry the query the merchant arrived on.
 */
export function scrubEventUrls(event) {
  if (!event?.properties) return event;

  const properties = Object.fromEntries(
    Object.entries(event.properties).map(([key, value]) =>
      /url/i.test(key) || key === '$referrer'
        ? [key, scrubSensitiveUrl(value)]
        : [key, value]
    )
  );

  return { ...event, properties };
}

let started = false;

export const isPostHogStarted = () => started;

/**
 * Starts PostHog with session replay. No-ops without `REACT_APP_POSTHOG_KEY`, so
 * local and unconfigured builds send nothing.
 *
 * Session replay has to be switched on for the project in PostHog as well — the
 * client asks for recording, the server decides whether to honour it.
 *
 * Called once from the entry point, before render.
 */
export function initPostHog() {
  const apiKey = process.env.REACT_APP_POSTHOG_KEY;
  if (started || !apiKey) return false;

  posthog.init(apiKey, {
    api_host: process.env.REACT_APP_POSTHOG_HOST || DEFAULT_API_HOST,
    // Only shops we identify get a person profile; anonymous pre-auth loads do
    // not each become their own person.
    person_profiles: 'identified_only',
    // Captured by hand on route change instead: this is a single-page app, so the
    // automatic one would only ever fire on the initial load.
    capture_pageview: false,
    // `before_send`, not `sanitize_properties`: the latter is deprecated in
    // posthog-js 1.410 and logs a warning on every init.
    before_send: scrubEventUrls,
    session_recording: {
      // Every API key in this app is typed into an input — Stocky, GoStock, the
      // QuickBooks fields — and the GoStock key box is not a password field, so
      // without this the replay would show the key in plain text.
      maskAllInputs: true,
    },
  });

  started = true;

  // The shop domain is the app's natural identity: every API call already sends
  // it as `shopify-shop-url`, and it is stable across sessions.
  const shop = new URLSearchParams(window.location.search).get('shop');
  if (shop) posthog.identify(shop, { shop });

  return true;
}

/** A route change in the SPA, reported as a pageview. */
export function capturePostHogPageview() {
  if (!started) return;
  posthog.capture('$pageview');
}

/** Product event, mirrored from `AnalyticsService`. Never throws at the caller. */
export function capturePostHogEvent(eventName, properties) {
  if (!started || !eventName) return;

  try {
    posthog.capture(eventName, properties);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('posthog capture failed', error);
  }
}

/**
 * Runs `fn` at most once per browser tab session for a given key, so an
 * impression-style event (e.g. a banner render) is not re-recorded on every
 * re-render, route change, or state update within the same session. Sibling
 * to `rememberGoStockTab` in `useGoStockFeature.js`, which does the equivalent
 * for the tab-unlock flag but needs `localStorage` because that trip leaves
 * the tab; this one never does, so `sessionStorage` is enough and resets
 * naturally on the merchant's next visit.
 */
const SESSION_ONCE_PREFIX = 'gostock:analytics-once:';

export function captureOncePerSession(key, fn) {
  try {
    const storageKey = `${SESSION_ONCE_PREFIX}${key}`;
    if (window.sessionStorage.getItem(storageKey)) return;
    window.sessionStorage.setItem(storageKey, '1');
  } catch {
    // A private-mode session that refuses storage just costs an extra event;
    // never let analytics plumbing block rendering over it.
  }
  fn();
}

export { posthog };
